import { AdminLayout, useMemoAsync, useSource } from "@opendash/core";
import { Icon } from "@opendash/icons";
import { useNavigate } from "@opendash/router";
import { AdminToolbar } from "@opendash/ui";
import { Button, Space, Table, Tag } from "antd";
import { ColumnType } from "antd/es/table";
import {
  SortOrder,
  SorterResult,
  TablePaginationConfig,
} from "antd/es/table/interface";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
import Parse from "parse";
import { useEffect, useState } from "react";
import { SIV_Order } from "../parsegenerated";

const PAGE_SIZE = 20;
const SORT_STORAGE_KEY = "siv-orders-overview-sort";

type OrderSortField = "startedAt" | "appointedAt" | "finishedAt";

function getStoredSort(): {
  sortField: OrderSortField | undefined;
  sortOrder: SortOrder | undefined;
} {
  try {
    const rawValue = localStorage.getItem(SORT_STORAGE_KEY);
    if (!rawValue) {
      return { sortField: undefined, sortOrder: undefined };
    }

    const parsed = JSON.parse(rawValue) as {
      sortField?: unknown;
      sortOrder?: unknown;
    };

    const field = parsed.sortField;
    const order = parsed.sortOrder;

    if (
      (field === "startedAt" ||
        field === "appointedAt" ||
        field === "finishedAt") &&
      (order === "ascend" || order === "descend")
    ) {
      return {
        sortField: field,
        sortOrder: order,
      };
    }
  } catch {
    // Ignore malformed localStorage data.
  }

  return { sortField: undefined, sortOrder: undefined };
}

type Props = {};

export const OrderOverviewPage = observer((props: Props) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<OrderSortField | undefined>(
    () => getStoredSort().sortField,
  );
  const [sortOrder, setSortOrder] = useState<SortOrder | undefined>(
    () => getStoredSort().sortOrder,
  );
  const [currentSelectedSource] = useSource();

  useEffect(() => {
    try {
      if (sortField && (sortOrder === "ascend" || sortOrder === "descend")) {
        localStorage.setItem(
          SORT_STORAGE_KEY,
          JSON.stringify({ sortField, sortOrder }),
        );
        return;
      }

      localStorage.removeItem(SORT_STORAGE_KEY);
    } catch {
      // Ignore localStorage write errors.
    }
  }, [sortField, sortOrder]);

  const result = useMemoAsync(
    async () => {
      const { results, count } = (await Parse.Cloud.run("siv-get-orders", {
        page,
        pageSize: PAGE_SIZE,
        search: searchTerm || undefined,
        sortField,
        sortOrder,
        currentSelectedSource: currentSelectedSource.id,
      })) as { results: SIV_Order[]; count: number };
      return { results, count };
    },
    [searchTerm, page, sortField, sortOrder, currentSelectedSource],
    { results: [], count: 0 },
  ) as { results: SIV_Order[]; count: number };

  const columns: ColumnType<SIV_Order>[] = [
    { title: "Auftragsnummer", dataIndex: "no", key: "orderNumber" },
    {
      title: "Gestartet am",
      dataIndex: "startedAt",
      key: "startedAt",
      render: (date: Date) => (date ? dayjs(date).format("DD.MM.YYYY") : "-"),
      sorter: true,
      sortOrder: sortField === "startedAt" ? sortOrder : undefined,
    },
    {
      title: "Terminiert für",
      dataIndex: "appointedAt",
      key: "appointedAt",
      render: (date: Date) => (date ? dayjs(date).format("DD.MM.YYYY") : "-"),
      sorter: true,
      sortOrder: sortField === "appointedAt" ? sortOrder : undefined,
    },
    {
      title: "Abgeschlossen am",
      dataIndex: "finishedAt",
      key: "finishedAt",
      render: (date: Date) => (date ? dayjs(date).format("DD.MM.YYYY") : "-"),
      sorter: true,
      sortOrder: sortField === "finishedAt" ? sortOrder : undefined,
    },
    {
      title: "Beschreibung",
      dataIndex: "description",
      key: "description",
      render: (text: string, order: SIV_Order) => {
        const customer = order.tenant?.get("label") || undefined;
        return (
          <Space>
            {text}
            {customer && <Tag>{customer}</Tag>}
          </Space>
        );
      },
    },
    {
      title: "Aktionen",
      dataIndex: "actions",
      key: "actions",
      render: (row, record: SIV_Order) => (
        <>
          <Button
            key="details"
            icon={<Icon icon="fa:eye" />}
            onClick={() => {
              try {
                sessionStorage.setItem(
                  "siv-orders-nav-list",
                  JSON.stringify(result.results.map((r) => ({ id: r.id, no: r.no }))),
                );
              } catch {}
              navigate(
                "/orders/details/" + record.id + "?hideSourcePicker=true",
              );
            }}
          />
        </>
      ),
    },
  ];

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: Record<string, unknown>,
    sorter: SorterResult<SIV_Order> | SorterResult<SIV_Order>[],
  ) => {
    const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const field = activeSorter?.field;
    const order = activeSorter?.order;

    let newSortField: OrderSortField | undefined;
    let newSortOrder: SortOrder | undefined;

    if (
      (field === "startedAt" ||
        field === "appointedAt" ||
        field === "finishedAt") &&
      (order === "ascend" || order === "descend")
    ) {
      newSortField = field;
      newSortOrder = order;
    }

    const sortChanged =
      newSortField !== sortField || newSortOrder !== sortOrder;

    setSortField(newSortField);
    setSortOrder(newSortOrder);
    setPage(sortChanged ? 1 : pagination.current || 1);
  };

  return (
    <AdminLayout>
      <AdminToolbar
        title="Aufträge"
        search={searchTerm}
        onSearch={(value) => {
          setPage(1);
          setSearchTerm(value);
        }}
      />
      <div className="od-page-main">
        <Table
          size="small"
          columns={columns}
          dataSource={result.results}
          rowKey={(record) => record.id!}
          onChange={handleTableChange}
          pagination={{
            pageSize: PAGE_SIZE,
            total: result.count,
            current: page,
            showSizeChanger: false,
          }}
        />
      </div>
    </AdminLayout>
  );
});
